/**
 * StudentLiveQuizScreen — le quiz de fin de séance, hors du live (`live-quiz`, `liveSessionId`,
 * `exitTo` et `activityPaused` en paramètres) : `<StudentLiveQuizPage>`.
 *
 * Poussé à la sortie du live (`useLiveClosing`), avant que le live s'éteigne : la route existe hors
 * du live, la pile qui bascule la garde. Il relit le quiz que la sortie a demandé ; sans quiz, il
 * mène tout de suite à la suite. La suite, c'est le second bouton de l'offre, la croix des
 * questions, « Continuer » au résultat et le retour d'Android : le rapport quand le tuteur a clôturé
 * (`live-end`, `exitTo: "report"` : l'élève est sorti de la salle, sa note donnée ou passée),
 * l'accueil quand l'élève est parti avant.
 */
import { useNavigation } from "@react-navigation/native"
import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { BackHandler } from "react-native"

import { StudentLiveQuizPage, type StudentLiveQuizExit } from "../../../../../../design-system/student"
import { useEndOfLiveQuiz } from "../../../../../../engine/live/student/useEndOfLiveQuiz"
import { createTelemetry } from "../../../../../../engine/shared/telemetry"
import { studentLiveQuizStep } from "../../../../view-models/student/Live/studentLiveQuizViewModel"
import { useBackOr } from "../../../hooks/useBackOr"
import { useStudentThemeScope } from "../../../hooks/useStudentThemeScope"
import { ParentStatusBar } from "../../parent/ParentStatusBar"
import { type StudentSpaceScreenProps } from "../studentSpaceRouteTypes"
import { useLiveQuizOfferTracking, type LiveQuizDeclineVia } from "./liveQuizTracking"

type Navigation = { replace: (route: string, params?: object) => void }

export function StudentLiveQuizScreen({ route }: StudentSpaceScreenProps) {
    useStudentThemeScope()
    const params = (route?.params ?? {}) as {
        liveSessionId?: number | string
        exitTo?: string
        activityPaused?: boolean | string
    }
    const liveSessionId = params.liveSessionId != null ? Number(params.liveSessionId) : null
    // Au web, un paramètre relu de l'adresse est une chaîne.
    const activityPaused = params.activityPaused === true || params.activityPaused === "true"
    const exitTo: StudentLiveQuizExit = params.exitTo === "report" ? "report" : "home"
    const navigation = useNavigation() as unknown as Navigation
    const backHome = useBackOr("menu")
    const telemetry = useMemo(() => createTelemetry("live-student.quiz", { role: "student" }), [])
    const play = useEndOfLiveQuiz(liveSessionId, telemetry)

    const goneRef = useRef(false)
    const goOn = useCallback(() => {
        if (goneRef.current) return
        goneRef.current = true
        if (exitTo === "report") navigation.replace("live-end", { liveSessionId, activityPaused, left: true })
        else backHome()
    }, [exitTo, liveSessionId, activityPaused, navigation, backHome])

    useEffect(() => {
        if (play.quiz === null) goOn()
    }, [play.quiz, goOn])

    const step = studentLiveQuizStep(play)
    const { trackStart, trackDecline } = useLiveQuizOfferTracking(
        step.kind === "offer" && play.quiz != null ? { ...step, quizId: play.quiz.id } : null,
        exitTo,
        play.from
    )
    const decline = (via: LiveQuizDeclineVia) => {
        trackDecline(via)
        goOn()
    }

    const backRef = useRef(decline)
    backRef.current = decline
    useEffect(() => {
        const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
            backRef.current("back")
            return true
        })
        return () => subscription.remove()
    }, [])

    return (
        <>
            <StudentLiveQuizPage
                step={step}
                exitTo={exitTo}
                onStart={() => {
                    trackStart()
                    play.start()
                }}
                onLater={() => decline("button")}
                onAnswer={play.answer}
                onNext={play.next}
                onLeave={goOn}
                onContinue={goOn}
            />
            <ParentStatusBar />
        </>
    )
}
